// src/components/gestionEvento/TeamSelector.js

import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useCompetencia } from '../../../../context/CompetenciaContext';

const TeamSelector = ({ value, onChange }) => {
  const { slug, competitionId } = useParams();
  const { competencias } = useCompetencia();
  const competition =
    competencias.find((comp) => comp.id === competitionId) ||
    competencias.find((comp) => comp.slug === slug);
  const [isNewTeam, setIsNewTeam] = useState(false);

  // Derivar equipos iniciales
  let derivedTeams = [];
  if (competition) {
    if (Array.isArray(competition.equipos) && competition.equipos.length > 0) {
      derivedTeams = competition.equipos;
    } else if (competition.inscripciones?.length > 0) {
      const uniqueTeamNames = Array.from(
        new Set(
          competition.inscripciones
            .map((insc) => {
              if (typeof insc.equipo === 'object' && insc.equipo !== null) {
                return insc.equipo.name;
              }
              return insc.equipo;
            })
            .filter((e) => typeof e === 'string' && e.trim() !== '')
        )
      );
      derivedTeams = uniqueTeamNames.map((name) => ({
        id: '',
        name,
        teamCode: '',
        logoEquipo: '',
      }));
    }
  }

  const [teams, setTeams] = useState(derivedTeams);
  const [selectedTeam, setSelectedTeam] = useState(
    typeof value === 'object' ? value : value ? { id: '', name: value } : null
  );
  const [inputValue, setInputValue] = useState(value?.name || '');

  useEffect(() => {
    if (!competition) {
      setTeams([]);
      return;
    }

    let newTeams = [];
    if (Array.isArray(competition.equipos)) {
      newTeams = competition.equipos;
    } else if (competition.inscripciones?.length > 0) {
      newTeams = Array.from(
        new Set(
          competition.inscripciones
            .map((insc) => insc.equipo?.name || insc.equipo)
            .filter(Boolean)
        )
      ).map((name) => ({ id: '', name }));
    }

    setTeams(newTeams);
  }, [competition]);

  const checkIfTeamExists = (teamName) => {
    return teams.some(
      (team) => team.name.toLowerCase() === teamName.toLowerCase()
    );
  };

  const handleTeamChange = (event, newValue) => {
    // Selección desde la lista
    setIsNewTeam(false);
    if (!newValue) {
      setSelectedTeam(null);
      setInputValue('');
      onChange?.(null);
      return;
    }
    // Si es opción existente (objeto), devolverla tal cual.
    if (typeof newValue === 'object') {
      setSelectedTeam(newValue);
      setInputValue(newValue.name || '');
      onChange?.(newValue);
      return;
    }
    // Si es un string tipeado y coincide con existente, resolver al objeto
    const existing = teams.find(t => t.name.toLowerCase() === String(newValue).toLowerCase());
    if (existing) {
      setSelectedTeam(existing);
      setInputValue(existing.name);
      onChange?.(existing);
      return;
    }
    // Nuevo nombre: mantener como texto; se creará al guardar
    setSelectedTeam({ id: '', name: newValue, teamCode: '', logoEquipo: '' });
    setInputValue(String(newValue));
    onChange?.(newValue);
  };

  const handleInputChange = (event, newInputValue, reason) => {
    setInputValue(newInputValue);
    const trimmedValue = newInputValue.trim();
    setIsNewTeam(!!trimmedValue && !checkIfTeamExists(trimmedValue));
    if (reason === 'input') {
      // Mantener el valor actual en el form: si hay texto, pasar el string;
      // si está vacío, limpiar.
      if (trimmedValue) {
        setSelectedTeam({ id: '', name: trimmedValue, teamCode: '', logoEquipo: '' });
        onChange?.(trimmedValue);
      } else {
        setSelectedTeam(null);
        onChange?.(null);
      }
    }
  };

  // En onBlur ya no se crea automáticamente el equipo
  const handleBlur = () => {
    // No se hace nada en onBlur; se espera la acción explícita del usuario
  };

  const renderInput = (params) => (
    <Box sx={{ width: '100%' }}>
      {isNewTeam && (
        <Typography variant="caption" sx={{ color: 'warning.main', mb: 1, display:'block' }}>
          Nuevo equipo. Se agregará al guardar la inscripción.
        </Typography>
      )}
      <TextField
        {...params}
        label="Equipo"
        placeholder="Selecciona o ingresa un equipo"
        size="small"
      />
    </Box>
  );

  return (
    <Autocomplete
      freeSolo
      openOnFocus
      options={teams}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.name || ''
      }
      value={selectedTeam}
      inputValue={inputValue}
      onChange={handleTeamChange}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      renderInput={renderInput}
      filterOptions={(options) => options}
    />
  );
};

export default TeamSelector;
